import { useState } from "react";
import { CalendarDays, ChevronDown, Layers, ListChecks } from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import GapGaugeBar from "./GapGaugeBar";
import MetricStatNumbers from "./MetricStatNumbers";
import { CategoryBadge, MetricCodeBadge } from "@/components/common";

const OrgDetailRow = ({ row, formatFrameworkLabel }) => {
  const { t } = useTran();
  const [isMonthlyOpen, setIsMonthlyOpen] = useState(false);
  const unit = row.unit || "";
  const monthlyBreakdown = Array.isArray(row.monthlyBreakdown)
    ? row.monthlyBreakdown
    : [];
  const hasMonthlyBreakdown = monthlyBreakdown.length > 0;
  const frameworkLabel = formatFrameworkLabel
    ? formatFrameworkLabel(row.framework)
    : row.framework;
  const showGauge = Number.isFinite(row.target) && Number.isFinite(row.actual);

  return (
    <div className="overflow-hidden rounded-md border border-border/60 bg-card/95">
      <div className="flex flex-col gap-3 p-3 md:flex-row md:items-center md:gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <ListChecks className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              {row.title}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
              <MetricCodeBadge code={row.code} category={row.category} />
              {row.year != null && (
                <span className="rounded border border-border/70 bg-muted/40 px-1.5 py-0.5 text-muted-foreground">
                  {row.year}
                </span>
              )}
              <span className="inline-flex items-center gap-1 rounded border border-border/70 bg-muted/40 px-1.5 py-0.5 text-muted-foreground">
                <Layers className="h-3 w-3" />
                {frameworkLabel}
              </span>
              <CategoryBadge category={row.category} />
            </div>
          </div>
        </div>
        <div className="flex min-w-0 flex-1 justify-end md:pr-12">
          <div className="min-w-0 max-w-[200px] flex-1">
            {showGauge ? <GapGaugeBar gapPct={row.gapPct} /> : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2">
          <MetricStatNumbers row={row} unit={unit} />
          {hasMonthlyBreakdown ? (
            <button
              type="button"
              onClick={() => setIsMonthlyOpen((open) => !open)}
              aria-label={isMonthlyOpen ? t("Collapse") : t("Expand")}
              title={isMonthlyOpen ? t("Collapse") : t("Expand")}
              className="inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
            >
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  isMonthlyOpen && "rotate-180",
                )}
              />
            </button>
          ) : null}
        </div>
      </div>

      {hasMonthlyBreakdown && isMonthlyOpen ? (
        <div className="border-t border-border/60">
          {monthlyBreakdown.map((monthRow) => (
            <div
              key={`${monthRow.year}-${monthRow.month}`}
              className="flex flex-col gap-3 border-b border-border/50 px-3 py-2.5 last:border-b-0 md:flex-row md:items-center md:gap-4"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border/80 bg-primary/5 text-primary">
                  <CalendarDays className="h-4 w-4" />
                </span>
                <p className="truncate text-xs font-semibold text-foreground">
                  {monthRow.label ||
                    `${monthRow.year} - ${String(monthRow.month).padStart(
                      2,
                      "0",
                    )}`}
                </p>
              </div>
              <div className="flex min-w-0 flex-1 justify-end md:pr-12">
                <div className="min-w-0 max-w-[200px] flex-1">
                  {Number.isFinite(monthRow.target) &&
                  Number.isFinite(monthRow.actual) ? (
                    <GapGaugeBar gapPct={monthRow.gapPct} />
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 items-center justify-end gap-2">
                <MetricStatNumbers row={monthRow} unit={unit} />
                <span className="h-8 w-8 shrink-0" aria-hidden="true" />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default OrgDetailRow;
